import type { Trip } from "@fairfleet/geotab";

/**
 * Selecting trips of more vehicles than this asks for confirmation first: every vehicle costs its
 * own LogRecord and StatusData request chains (the owner may tune this number).
 */
export const CONFIRM_VEHICLE_THRESHOLD = 10;

/** Number of distinct vehicles among the trips with the given ids. */
export function countSelectedVehicles(trips: Pick<Trip, "id" | "device">[], selectedIds: Iterable<string>): number {
  const ids = new Set(selectedIds);
  const vehicles = new Set<string>();
  for (const trip of trips) {
    if (ids.has(trip.id) && trip.device?.id) vehicles.add(trip.device.id);
  }
  return vehicles.size;
}

/** True when the selection grew and now spans more vehicles than {@link CONFIRM_VEHICLE_THRESHOLD}. */
export function needsSelectionConfirm(
  trips: Pick<Trip, "id" | "device">[],
  previousIds: string[],
  nextIds: string[],
  threshold = CONFIRM_VEHICLE_THRESHOLD,
): boolean {
  if (nextIds.length <= previousIds.length) return false;
  return countSelectedVehicles(trips, nextIds) > threshold;
}

/** A selection spanning more vehicles than this is refused: each vehicle loads its own LogRecord and StatusData series. */
export const MAX_SELECTED_VEHICLES = 50;

/** True when the selection grew and now spans more vehicles than {@link MAX_SELECTED_VEHICLES}. */
export function exceedsVehicleCap(
  trips: Pick<Trip, "id" | "device">[],
  previousIds: string[],
  nextIds: string[],
  cap = MAX_SELECTED_VEHICLES,
): boolean {
  return nextIds.length > previousIds.length && countSelectedVehicles(trips, nextIds) > cap;
}
